import {
  LayerProps,
  DataDrivenPropertyValueSpecification,
  FilterSpecification,
} from 'shared/map-exports';
import {
  ActivePrescribedFireIcon,
  ContainedFireIcon,
  InactiveFireIcon,
  ScheduledFireIcon,
  SilentActiveFireIcon,
  SilentInactiveFireIcon,
} from 'features/mapping/icons';
import { FONT_NAMES } from 'features/mapping/styles';
import {
  PRESCRIBED_FIRES_LABEL_MIN_ZOOM,
  WILDFIRE_INACTIVE_LABELS_MIN_ZOOM,
  WILDFIRE_LABELS_MIN_ZOOM,
} from './WildfiresLayer.constants';

/**
 * Control label visibility via opacity.
 *
 * @returns an expression that shows the element when zoomed in beyond the labelMinZoomLevel
 * and hides it if not. Selected geo event elements are always visible. Note that labels never
 * adopt partial opacity like their respective icons do: always either 1.0 or 0.0.
 * Currently only used for complex wildfire incidents: text number displayed inside the complex icon.
 */
const getLabelVisibilityExpression = (
  selectedGeoEventId: number,
): DataDrivenPropertyValueSpecification<number> => [
  'step',
  ['zoom'],
  // below WILDFIRE_LABELS_MIN_ZOOM, only show if selected
  ['case', ['==', ['get', 'id'], selectedGeoEventId], 1, 0],
  // starting at WILDFIRE_LABELS_MIN_ZOOM, show if selected, if
  // a wildfire (not isPrescribed), or if associated icon has any opacity.
  WILDFIRE_LABELS_MIN_ZOOM,
  [
    'case',
    ['==', ['get', 'id'], selectedGeoEventId], // if selected, show
    1,
    ['get', 'isPrescribed'], // if isPrescribed, hide
    0,
    ['==', ['get', 'opacity'], 0], // if icon completely invisible at all, hide
    0,
    ['!', ['get', 'isActive']], // if not active, hide
    0,
    1, // fallback
  ],
  WILDFIRE_INACTIVE_LABELS_MIN_ZOOM,
  // starting at WILDFIRE_INACTIVE_LABELS_MIN_ZOOM, show if selected, if
  // a wildfire (not isPrescribed), or if associated icon has any opacity.
  [
    'case',
    ['==', ['get', 'id'], selectedGeoEventId], // if selected, show
    1,
    ['get', 'isPrescribed'], // if isPrescribed, hide
    0,
    ['==', ['get', 'opacity'], 0], // if icon completely invisible, hide
    0,
    1, // fallback
  ],
  // starting at PRESCRIBED_FIRES_LABEL_MIN_ZOOM, show if selected, or if associated
  // icon has any opacity.
  PRESCRIBED_FIRES_LABEL_MIN_ZOOM,
  [
    'case',
    ['==', ['get', 'id'], selectedGeoEventId],
    1,
    ['==', ['get', 'opacity'], 0], // if icon completely invisible at all, hide
    0,
    1, // fallback
  ],
];

/**
 * Control label visibility via a filter.
 *
 * @returns a boolean expression that renders the element when zoomed in beyond the
 * labelMinZoomLevel and filters it out if not. Selected geo event elements are always
 * visible.
 */
const getLabelFilterExpression = (
  selectedGeoEventId: number,
): FilterSpecification => [
  'step',
  ['zoom'],
  // below WILDFIRE_LABELS_MIN_ZOOM: only show if selected
  ['==', ['get', 'id'], selectedGeoEventId],
  WILDFIRE_LABELS_MIN_ZOOM,
  // starting at WILDFIRE_LABELS_MIN_ZOOM: show if selected OR (not prescribed AND has opacity AND active)
  [
    'any',
    ['==', ['get', 'id'], selectedGeoEventId],
    [
      'all',
      ['!', ['get', 'isPrescribed']], // is not prescribed
      ['!=', ['get', 'opacity'], 0], // has opacity
      ['get', 'isActive'], // is active
    ],
  ],
  WILDFIRE_INACTIVE_LABELS_MIN_ZOOM,
  // starting at WILDFIRE_INACTIVE_LABELS_MIN_ZOOM: show if selected OR  (not prescribed AND has opacity)
  [
    'any',
    ['==', ['get', 'id'], selectedGeoEventId],
    [
      'all',
      ['!', ['get', 'isPrescribed']], // is not prescribed
      ['!=', ['get', 'opacity'], 0], // has opacity
    ],
  ],
  PRESCRIBED_FIRES_LABEL_MIN_ZOOM,
  // starting at PRESCRIBED_FIRES_LABEL_MIN_ZOOM: show if selected OR has opacity
  [
    'any',
    ['==', ['get', 'id'], selectedGeoEventId],
    ['!=', ['get', 'opacity'], 0],
  ],
];

/**
 * Get the opacity for a geo event icon. Hides the icon when in edit mode and
 * shows it when selected. Otherwise, falls back to 'opacity' property value.
 */
const getIconOpacityExpression = (
  selectedGeoEventId: number,
  isEdit: boolean,
): DataDrivenPropertyValueSpecification<number> => [
  'case',
  [
    'all',
    ['==', ['get', 'id'], selectedGeoEventId],
    ['==', ['get', 'opacity'], 0],
  ],
  1,
  // hide this icon on the edit page since <Map.WildFireGeoEventMarker> appears instead
  [
    'case',
    ['all', ['==', isEdit, true], ['==', ['get', 'id'], selectedGeoEventId]],
    0,
    ['get', 'opacity'],
  ],
];

/**
 * Ensure selected label is always on top if selected
 */
const getLabelSortExpression = (
  selectedGeoEventId: number,
): DataDrivenPropertyValueSpecification<number> => [
  'case',
  ['==', ['get', 'id'], selectedGeoEventId],
  -99999,
  ['get', 'indexInv'],
];

export const getIconStyle = (
  selectedGeoEventId: number,
  isEdit: boolean,
): LayerProps => ({
  filter: ['==', ['get', 'isComplexParent'], false],
  type: 'symbol',
  layout: {
    'icon-image': [
      'case',
      ['==', ['get', 'id'], selectedGeoEventId],
      ['get', 'selectedIcon'],
      ['get', 'icon'],
    ],
    'icon-allow-overlap': true,
    'icon-anchor': 'bottom',
    'icon-ignore-placement': false,
    'icon-size': [
      'interpolate',
      ['linear'],
      ['zoom'],
      4,
      [
        'match',
        ['get', 'icon'],
        SilentActiveFireIcon.name,
        ['*', ['get', 'scale'], 0.6],
        SilentInactiveFireIcon.name,
        ['*', ['get', 'scale'], 0.6],
        ActivePrescribedFireIcon.name,
        ['*', ['get', 'scale'], 0.6],
        ScheduledFireIcon.name,
        ['*', ['get', 'scale'], 0.6],
        InactiveFireIcon.name,
        ['*', ['get', 'scale'], 0.6],
        ContainedFireIcon.name,
        ['*', ['get', 'scale'], 0.6],
        ['*', ['get', 'scale'], 0.9],
      ],
      8,
      ['get', 'scale'],
    ],
    'icon-offset': [
      ['get', 'offsetX'],
      ['get', 'offsetY'],
    ],

    // For some reason, the icons need to be sorted by 'index',
    // but the labels by 'indexInv', in order to be sorted correctly
    // in both cases.
    'symbol-sort-key': ['get', 'index'],
    'symbol-z-order': 'source',
  },
  paint: {
    'icon-opacity': getIconOpacityExpression(selectedGeoEventId, isEdit),
  },
});

export const getComplexIconStyle = (
  selectedGeoEventId: number,
  isEdit: boolean,
): LayerProps => {
  const normalIconStyling = getIconStyle(selectedGeoEventId, isEdit);

  return {
    ...normalIconStyling,
    filter: ['==', ['get', 'isComplexParent'], true],
    type: 'symbol',
    layout: {
      ...normalIconStyling.layout,

      // Center the complex icon so the number we show on it is centered
      'icon-anchor': 'center',

      // Needed to prevent icon disappearing when other labels overlap the child count text within this icon
      'text-allow-overlap': true,

      // Show number of children over complex icon - but not when editing since
      // we show a different draggable icon (<Map.WildFireGeoEventMarker)
      'text-font': FONT_NAMES.bold,
      'text-size': ['*', ['get', 'scale'], 10],
      'text-offset': [0, 0.2],
      ...(isEdit ? {} : { 'text-field': ['get', 'complexChildrenCount'] }),
    },
    paint: {
      ...normalIconStyling.paint,
      'text-opacity': getLabelVisibilityExpression(selectedGeoEventId),
    },
  };
};

export const getComplexLabelStyle = (
  selectedGeoEventId: number,
): LayerProps => ({
  filter: [
    'all',
    ['==', ['get', 'isComplexParent'], true],
    getLabelFilterExpression(selectedGeoEventId),
  ],
  type: 'symbol',
  layout: {
    'text-font': FONT_NAMES.bold,
    'text-transform': 'uppercase',
    'text-field': ['get', 'name'],
    'text-anchor': 'top',
    'text-justify': 'center',
    'symbol-sort-key': getLabelSortExpression(selectedGeoEventId),
    'symbol-z-order': 'source',
    'icon-image': [
      'case',
      ['get', 'isActive'],
      ['literal', 'ComplexColorSquare'],
      ['literal', 'InactiveComplexColorSquare'],
    ],

    'icon-text-fit': 'both',
    'text-size': 11,
    'text-offset': [
      ['get', 'complexChipOffsetX'],
      ['get', 'complexChipOffsetY'],
    ],
  },
});

const labelStyleCommon: LayerProps = {
  type: 'symbol',
  layout: {
    'text-font': FONT_NAMES.regular,
    'text-field': [
      'format',
      ['get', 'name'],
      { 'text-font': ['literal', FONT_NAMES.bold] },
      '\n',
      {},
      ['get', 'acreageStr'],
      { 'font-scale': 0.7 },
      '\n',
      {},
    ],
    'text-anchor': 'top',
    'text-justify': 'center',
    'symbol-sort-key': ['get', 'indexInv'],
    'symbol-z-order': 'source',
    'icon-image': '',
    'icon-text-fit': 'both',
    'text-size': 16,
    'text-offset': [
      'case',
      ['get', 'isComplexChild'],
      ['literal', [0, 1.8]], // make room for the complex chip above this
      ['literal', [0, 0]],
    ],
  },
  paint: {
    'text-halo-width': 1.8,
  },
};

const labelStyleLight: LayerProps = {
  ...labelStyleCommon,
  paint: {
    ...labelStyleCommon.paint,
    'text-color': '#333333',
    'text-halo-color': 'rgba(255,255,255,0.8)',
  },
};

const labelStyleDark: LayerProps = {
  ...labelStyleCommon,
  paint: {
    ...labelStyleCommon.paint,
    'text-color': '#ffffff',
    'text-halo-color': 'rgba(0,0,0,0.8)',
  },
};

export const getLabelStyle = (
  lightBaseLayer: boolean,
  selectedGeoEventId: number,
): LayerProps => {
  const { layout, ...style } = lightBaseLayer
    ? labelStyleLight
    : labelStyleDark;

  return {
    ...style,
    filter: [
      'all',
      ['==', ['get', 'isComplexParent'], false],
      getLabelFilterExpression(selectedGeoEventId),
    ],
    layout: {
      ...layout,
      'symbol-sort-key': getLabelSortExpression(selectedGeoEventId),
    },
    paint: {
      ...style.paint,
    },
  };
};

/**
 * This styling adds an incident's parent complex incident as a chip below the
 * incident's normal label using a naive offset.
 */
export const getComplexChildIncidentChipStyle = (
  selectedGeoEventId: number,
): LayerProps => ({
  filter: [
    'all',
    ['==', ['get', 'isComplexChild'], true],
    getLabelFilterExpression(selectedGeoEventId),
  ],
  type: 'symbol',
  layout: {
    'text-font': FONT_NAMES.bold,
    'text-transform': 'uppercase',
    'text-field': ['get', 'complexParentName'],
    'text-max-width': 100,
    'text-anchor': 'top',
    'text-justify': 'center',
    'symbol-sort-key': ['get', 'indexInv'],
    'symbol-z-order': 'source',
    'icon-image': [
      'case',
      ['get', 'isActive'],
      ['literal', 'ComplexColorSquare'],
      ['literal', 'InactiveComplexColorSquare'],
    ],
    'icon-text-fit': 'both',
    'text-size': 8,
    'text-offset': [0, 1],
  },
});
